/* Hera Collective — theme behaviour.
   Everything here is progressive enhancement: filters and forms all work as
   plain GET/POST without it. Loaded with `defer`, so the DOM is ready. */
(function () {
  'use strict';

  /* ---- signup popup ------------------------------------------------------ */
  var popup = document.querySelector('[data-signup-popup]');
  var PENDING_KEY = 'hc:popup:pending';
  var UNTIL_KEY = 'hc:popup:until';

  function openPopup() {
    if (!popup) return;
    popup.setAttribute('data-open', '');
    var panel = popup.querySelector('.popup__panel');
    var first = panel.querySelector('input, button.popup__close');
    if (first) first.focus();
    document.addEventListener('keydown', onPopupKeydown);
  }

  function closePopup() {
    if (!popup) return;
    popup.removeAttribute('data-open');
    document.removeEventListener('keydown', onPopupKeydown);
    try {
      var days = parseInt(popup.dataset.frequency || '30', 10);
      localStorage.setItem(UNTIL_KEY, String(Date.now() + days * 864e5));
      localStorage.removeItem(PENDING_KEY);
    } catch (e) { /* private mode */ }
  }

  function onPopupKeydown(event) {
    if (event.key === 'Escape') closePopup();
  }

  if (popup) {
    popup.querySelectorAll('[data-popup-close]').forEach(function (el) {
      el.addEventListener('click', closePopup);
    });

    // The native (Shopify customer form) path reloads the page on submit.
    // A pending flag marks that THIS popup's form was the one submitted, so
    // the reopened popup is showing its success or errors — not the footer's.
    var nativeForm = popup.querySelector('form[action*="/contact"]');
    if (nativeForm) {
      nativeForm.addEventListener('submit', function () {
        try { localStorage.setItem(PENDING_KEY, '1'); } catch (e) { /* ok */ }
      });
    }

    var pending = null;
    var until = 0;
    try {
      pending = localStorage.getItem(PENDING_KEY);
      until = parseInt(localStorage.getItem(UNTIL_KEY) || '0', 10);
    } catch (e) { /* private mode: show per session */ }

    var posted = popup.querySelector('[data-popup-posted]');
    if (pending && posted) {
      openPopup();
      try { localStorage.removeItem(PENDING_KEY); } catch (e) { /* ok */ }
    } else if (Date.now() > until) {
      var delay = parseInt(popup.dataset.delay || '6', 10) * 1000;
      setTimeout(openPopup, delay);
    }

    // Klaviyo path: posts email + phone with consent to the client API,
    // which records marketing consent compliantly. 202 = accepted.
    var klaviyoForm = popup.querySelector('[data-popup-klaviyo]');
    if (klaviyoForm) {
      klaviyoForm.addEventListener('submit', function (event) {
        event.preventDefault();
        var errorEl = klaviyoForm.querySelector('[data-popup-error]');
        var successEl = klaviyoForm.querySelector('[data-popup-success]');
        var email = (klaviyoForm.elements.email.value || '').trim();
        var phoneInput = klaviyoForm.elements.phone;
        var phone = phoneInput ? (phoneInput.value || '').trim() : '';
        var consentInput = klaviyoForm.elements.sms_consent;
        var smsConsent = consentInput ? consentInput.checked : false;

        errorEl.hidden = true;
        if (!email) {
          errorEl.textContent = 'An email address is needed.';
          errorEl.hidden = false;
          return;
        }
        if (phone && !smsConsent) {
          errorEl.textContent = 'To receive texts, please tick the consent box — or clear the phone field.';
          errorEl.hidden = false;
          return;
        }

        var attributes = {
          email: email,
          subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } }
        };
        if (phone && smsConsent) {
          attributes.phone_number = phone;
          attributes.subscriptions.sms = { marketing: { consent: 'SUBSCRIBED' } };
        }

        fetch(
          'https://a.klaviyo.com/client/subscriptions?company_id=' +
            encodeURIComponent(popup.dataset.klaviyoCompany),
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/vnd.api+json',
              Revision: '2026-07-15'
            },
            body: JSON.stringify({
              data: {
                type: 'subscription',
                attributes: {
                  custom_source: 'Theme signup popup',
                  profile: { data: { type: 'profile', attributes: attributes } }
                },
                relationships: {
                  list: { data: { type: 'list', id: popup.dataset.klaviyoList } }
                }
              }
            })
          }
        )
          .then(function (response) {
            if (response.status === 202) {
              klaviyoForm.querySelectorAll('div, .btn--solid, .popup__consent, .popup__fine').forEach(function (el) {
                el.hidden = true;
              });
              successEl.hidden = false;
              try {
                localStorage.setItem(UNTIL_KEY, String(Date.now() + 365 * 864e5));
              } catch (e) { /* ok */ }
            } else {
              return response.json().then(function (body) {
                var detail = body && body.errors && body.errors[0] && body.errors[0].detail;
                throw new Error(detail || 'Something went sideways — please try again.');
              });
            }
          })
          .catch(function (error) {
            errorEl.textContent = error.message || 'Something went sideways — please try again.';
            errorEl.hidden = false;
          });
      });
    }
  }

  /* ---- filter drawer ----------------------------------------------------- */
  var facets = document.querySelector('[data-facets]');
  var facetsOpenBtn = document.querySelector('[data-facets-open]');

  function closeFacets() {
    if (!facets) return;
    facets.removeAttribute('data-open');
    if (facetsOpenBtn) {
      facetsOpenBtn.setAttribute('aria-expanded', 'false');
      facetsOpenBtn.focus();
    }
    document.removeEventListener('keydown', onFacetsKeydown);
  }

  function onFacetsKeydown(event) {
    if (event.key === 'Escape') closeFacets();
  }

  if (facets && facetsOpenBtn) {
    facetsOpenBtn.addEventListener('click', function () {
      // The panel is in flow, so this is a disclosure toggle, not a modal.
      if (facets.hasAttribute('data-open')) { closeFacets(); return; }
      facets.setAttribute('data-open', '');
      facetsOpenBtn.setAttribute('aria-expanded', 'true');
      var first = facets.querySelector('summary, input');
      if (first) first.focus({ preventScroll: true });
      document.addEventListener('keydown', onFacetsKeydown);
    });
    facets.querySelectorAll('[data-facets-close]').forEach(function (el) {
      el.addEventListener('click', closeFacets);
    });
    // Checkbox choices apply immediately; price inputs apply on change
    // (blur / Enter). The Apply button stays for keyboards and no-JS.
    facets.querySelectorAll('input').forEach(function (input) {
      input.addEventListener('change', function () {
        var form = facets.querySelector('[data-facets-form]');
        if (form.requestSubmit) form.requestSubmit();
        else form.submit();
      });
    });
  }

  /* ---- sort -------------------------------------------------------------- */
  document.querySelectorAll('[data-sort-select]').forEach(function (select) {
    select.addEventListener('change', function () {
      var url = new URL(window.location.href);
      url.searchParams.set('sort_by', select.value);
      url.searchParams.delete('page');
      window.location.assign(url.toString());
    });
  });

  /* ---- featured collection switcher ------------------------------------- */
  function initCollectionSwitchers(root) {
    if (!root || !root.querySelectorAll) return;

    var switchers = Array.prototype.slice.call(root.querySelectorAll('[data-collection-switcher]'));
    if (root.matches && root.matches('[data-collection-switcher]')) switchers.unshift(root);

    switchers.forEach(function (section) {
      if (section.dataset.hcCollectionSwitcherReady) return;

      var panels = Array.prototype.slice.call(section.querySelectorAll('[data-collection-panel]'));
      var previousButton = section.querySelector('[data-collection-prev]');
      var nextButton = section.querySelector('[data-collection-next]');
      var headingLink = section.querySelector('[data-collection-heading-link]');
      var viewAllLink = section.querySelector('[data-collection-view-all]');
      var status = section.querySelector('[data-collection-status]');
      if (!panels.length || !headingLink || !viewAllLink) return;

      section.dataset.hcCollectionSwitcherReady = 'true';
      var currentIndex = panels.findIndex(function (panel) { return !panel.hidden; });
      if (currentIndex < 0) currentIndex = 0;
      var switching = false;
      var reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      function wrapIndex(index) {
        return (index + panels.length) % panels.length;
      }

      function panelTitle(index) {
        return panels[wrapIndex(index)].dataset.collectionTitle || 'Collection';
      }

      function updateControlLabels() {
        var hasChoices = panels.length > 1;
        if (previousButton) {
          previousButton.hidden = !hasChoices;
          previousButton.setAttribute('aria-label', 'Previous collection: ' + panelTitle(currentIndex - 1));
        }
        if (nextButton) {
          nextButton.hidden = !hasChoices;
          nextButton.setAttribute('aria-label', 'Next collection: ' + panelTitle(currentIndex + 1));
        }
      }

      function commitPanel(index, announce) {
        currentIndex = wrapIndex(index);
        var activePanel = panels[currentIndex];
        var title = activePanel.dataset.collectionTitle || 'Collection';
        var url = activePanel.dataset.collectionUrl || '/collections/all';
        var handle = activePanel.dataset.collectionHandle || '';

        panels.forEach(function (panel, panelIndex) {
          var isActive = panelIndex === currentIndex;
          panel.hidden = !isActive;
          if (isActive) {
            panel.removeAttribute('inert');
            panel.removeAttribute('aria-hidden');
          } else {
            panel.setAttribute('inert', '');
            panel.setAttribute('aria-hidden', 'true');
          }
        });

        headingLink.textContent = title;
        headingLink.setAttribute('href', url);
        viewAllLink.setAttribute('href', url);
        viewAllLink.setAttribute('aria-label', 'View all ' + title.replace(/^Shop\s+/i, ''));
        section.dataset.activeCollection = handle;
        updateControlLabels();

        if (announce && status) {
          status.textContent = title + ', collection ' + (currentIndex + 1) + ' of ' + panels.length;
        }
      }

      function showPanel(index, options) {
        options = options || {};
        var targetIndex = wrapIndex(index);
        if (!options.force && (targetIndex === currentIndex || switching)) return;

        if (options.immediate || reducedMotion) {
          section.classList.remove('is-switching');
          section.removeAttribute('aria-busy');
          switching = false;
          commitPanel(targetIndex, options.announce !== false);
          return;
        }

        switching = true;
        section.setAttribute('aria-busy', 'true');
        section.classList.add('is-switching');

        window.setTimeout(function () {
          commitPanel(targetIndex, true);
          window.requestAnimationFrame(function () {
            section.classList.remove('is-switching');
            window.setTimeout(function () {
              section.removeAttribute('aria-busy');
              switching = false;
            }, 140);
          });
        }, 140);
      }

      if (previousButton) {
        previousButton.addEventListener('click', function () { showPanel(currentIndex - 1); });
      }
      if (nextButton) {
        nextButton.addEventListener('click', function () { showPanel(currentIndex + 1); });
      }

      section.addEventListener('shopify:block:select', function (event) {
        var blockId = event.detail && event.detail.blockId;
        var panelIndex = panels.findIndex(function (panel) {
          return blockId && panel.dataset.blockId === blockId;
        });
        if (panelIndex < 0 && event.target.closest) {
          var selectedPanel = event.target.closest('[data-collection-panel]');
          panelIndex = panels.indexOf(selectedPanel);
        }
        if (panelIndex >= 0) showPanel(panelIndex, { immediate: true, force: true });
      });

      commitPanel(currentIndex, false);
    });
  }

  initCollectionSwitchers(document);
  document.addEventListener('shopify:section:load', function (event) {
    initCollectionSwitchers(event.target);
  });

  /* ---- product gallery ---------------------------------------------------- */
  var mainImage = document.querySelector('[data-pdp-main]');
  var thumbs = Array.prototype.slice.call(document.querySelectorAll('[data-pdp-thumb]'));

  function showMedia(index) {
    if (!mainImage || !thumbs.length) return;
    var next = thumbs[(index + thumbs.length) % thumbs.length];
    mainImage.src = next.dataset.src;
    mainImage.srcset = next.dataset.srcset;
    mainImage.alt = next.dataset.alt;
    thumbs.forEach(function (thumb) { thumb.removeAttribute('aria-current'); });
    next.setAttribute('aria-current', 'true');
  }

  function currentMediaIndex() {
    return Math.max(0, thumbs.findIndex(function (thumb) {
      return thumb.getAttribute('aria-current') === 'true';
    }));
  }

  if (mainImage && thumbs.length) {
    thumbs.forEach(function (thumb, index) {
      thumb.addEventListener('click', function () { showMedia(index); });
    });
    var prev = document.querySelector('[data-pdp-prev]');
    var next = document.querySelector('[data-pdp-next]');
    if (prev) prev.addEventListener('click', function () { showMedia(currentMediaIndex() - 1); });
    if (next) next.addEventListener('click', function () { showMedia(currentMediaIndex() + 1); });
  }

  /* ---- Wishlist by Square accessibility --------------------------------- */
  // Square renders its save actions as clickable divs. Preserve the app's
  // own click/state logic while giving those controls keyboard semantics.
  var wishlistControlSelector = [
    '.Vtl-WishlistButton__WishlistIconButton',
    '.Vtl-WishlistButton__AddToWishlist',
    '.Vtl-WishlistButton__GoToWishlist'
  ].join(',');

  function enhanceWishlistControls(root) {
    if (!root || !root.querySelectorAll) return;
    var controls = Array.prototype.slice.call(root.querySelectorAll(wishlistControlSelector));
    if (root.matches && root.matches(wishlistControlSelector)) controls.unshift(root);

    controls.forEach(function (control) {
      if (control.dataset.hcWishlistA11y) return;
      control.dataset.hcWishlistA11y = 'true';

      if (control.tagName !== 'A' && control.tagName !== 'BUTTON') {
        control.setAttribute('role', 'button');
        control.setAttribute('tabindex', '0');
        control.addEventListener('keydown', function (event) {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          control.click();
        });
      }

      if (!control.hasAttribute('aria-label')) {
        var label = control.getAttribute('title') || control.textContent.trim();
        control.setAttribute('aria-label', label || 'Wishlist');
      }
    });
  }

  function syncWishlistRoutes() {
    var appLink = document.querySelector('#vitals-wishlist-header a[href], a.Vtl-WishlistHeader[href]');
    if (!appLink) return;
    document.querySelectorAll('[data-hc-wishlist-route]').forEach(function (link) {
      link.setAttribute('href', appLink.getAttribute('href'));
    });
  }

  function initPdpWishlistButton() {
    var button = document.querySelector('[data-hc-square-pdp-button]');
    if (!button) return;

    var label = button.querySelector('[data-hc-square-pdp-button-label]');

    function syncButton() {
      var officialControl = document.querySelector('#vitals-wishlist .Vtl-WishlistButton__AddToWishlist');
      var squareControl = document.querySelector('.pdp__wishlist-square-bridge .Vtl-WishlistButton__WishlistIconButton');
      button.hidden = Boolean(officialControl);
      button.disabled = !squareControl;
      if (!squareControl) return null;

      var squareLabel = squareControl.getAttribute('title') || squareControl.getAttribute('aria-label') || '';
      var isAdded = /added to wishlist/i.test(squareLabel);
      var nextLabel = isAdded ? 'Added to Wishlist' : 'Add to Wishlist';
      button.setAttribute('aria-pressed', isAdded ? 'true' : 'false');
      if (label && label.textContent !== nextLabel) label.textContent = nextLabel;
      return squareControl;
    }

    var attempts = 0;
    var readyTimer = window.setInterval(function () {
      attempts += 1;
      if (syncButton() || attempts >= 40) window.clearInterval(readyTimer);
    }, 250);

    button.addEventListener('click', function () {
      var squareControl = syncButton();
      if (!squareControl) return;
      button.disabled = true;
      squareControl.click();
      window.setTimeout(syncButton, 350);
      window.setTimeout(syncButton, 1200);
    });

    syncButton();
  }

  enhanceWishlistControls(document);
  syncWishlistRoutes();
  initPdpWishlistButton();
  if (window.MutationObserver && document.body) {
    new MutationObserver(function (records) {
      records.forEach(function (record) {
        Array.prototype.forEach.call(record.addedNodes, function (node) {
          if (node.nodeType === 1) enhanceWishlistControls(node);
        });
      });
      syncWishlistRoutes();
    }).observe(document.body, { childList: true, subtree: true });
  }

  /* ---- cart quantity steppers --------------------------------------------- */
  document.querySelectorAll('.qty').forEach(function (qty) {
    var input = qty.querySelector('[data-qty-input]');
    var form = qty.closest('form');
    if (!input || !form) return;

    function step(delta) {
      var value = (parseInt(input.value, 10) || 0) + delta;
      var min = parseInt(input.min || '0', 10);
      var max = input.max ? parseInt(input.max, 10) : Infinity;
      input.value = Math.min(max, Math.max(min, value));
      if (form.requestSubmit) form.requestSubmit();
      else form.submit();
    }

    var minus = qty.querySelector('[data-qty-minus]');
    var plus = qty.querySelector('[data-qty-plus]');
    if (minus) minus.addEventListener('click', function () { step(-1); });
    if (plus) plus.addEventListener('click', function () { step(1); });
    input.addEventListener('change', function () {
      if (form.requestSubmit) form.requestSubmit();
      else form.submit();
    });
  });

  /* ---- related products (recommendations endpoint) ------------------------ */
  var related = document.querySelector('[data-related]');
  if (related && related.dataset.url) {
    fetch(related.dataset.url)
      .then(function (response) { return response.text(); })
      .then(function (text) {
        var fragment = new DOMParser().parseFromString(text, 'text/html');
        var fresh = fragment.querySelector('[data-related-content]');
        var slot = related.querySelector('[data-related-content]');
        if (fresh && slot && fresh.children.length) {
          slot.innerHTML = fresh.innerHTML;
        } else if (slot && !slot.children.length) {
          related.style.display = 'none';
        }
      })
      .catch(function () { /* leave as rendered */ });
  }
})();
